import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  buildPrescriptionFields,
  resolveExerciseDisplayName,
} from "../utils/printablePlanFormatting";
import { getPrescriptionLabel, buildPrescriptionSummary } from "../utils/exerciseFormatting";
import ExerciseNameLink from "./ExerciseNameLink";

function PrescriptionGrid({ exercise }) {
  const fields = buildPrescriptionFields(exercise);
  if (!fields.length) return null;

  return (
    <div className="pp-prescription-grid" aria-label="Prescription details">
      {fields.map(({ label, value }) => (
        <div key={`${label}-${value}`} className="pp-prescription-stat">
          <span className="pp-prescription-label">{label}</span>
          <span className="pp-prescription-value">{value}</span>
        </div>
      ))}
    </div>
  );
}

function NotesBlock({ label, content }) {
  if (!content || (typeof content === "string" && !content.trim())) return null;
  return (
    <div className="pp-notes-block">
      <span className="pp-notes-label">{label}</span>
      <div className="pp-notes-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </div>
  );
}

function PlanAlternatives({ alternatives, findById, getExerciseName, linkExerciseNames }) {
  const items = alternatives || [];
  if (!items.length) return null;

  return (
    <div className="pp-alternatives">
      <div className="pp-alternatives-title">Alternative exercises</div>
      <ul className="pp-alternatives-list">
        {items.map((alt, idx) => {
          const altId =
            alt?.exercise != null && typeof alt.exercise === "object"
              ? alt.exercise.id ?? alt.exercise.exerciseId
              : alt?.exercise;
          const name = resolveExerciseDisplayName(alt, findById, getExerciseName);
          const fields = buildPrescriptionFields(alt);
          const notes =
            typeof alt.notes === "string" && alt.notes.trim() ? alt.notes.trim() : null;

          return (
            <li key={alt.id ?? `alt-${idx}`} className="pp-alternative-item">
              {linkExerciseNames ? (
                <ExerciseNameLink
                  exerciseId={altId}
                  name={name}
                  className="pp-alternative-name"
                />
              ) : (
                <span className="pp-alternative-name">{name}</span>
              )}
              {fields.length > 0 && (
                <div className="pp-alternative-stats">
                  {fields.map(({ label, value }) => (
                    <span key={`${label}-${value}`} className="pp-alternative-stat">
                      <strong>{label}:</strong> {value}
                    </span>
                  ))}
                </div>
              )}
              {notes ? <p className="pp-alternative-notes">{notes}</p> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ExerciseTitle({ exerciseId, name, sub = false, linkExerciseNames }) {
  const className = sub ? "pp-exercise-name pp-exercise-name--sub" : "pp-exercise-name";
  if (linkExerciseNames) {
    return <ExerciseNameLink exerciseId={exerciseId} name={name} className={className} />;
  }
  const Tag = sub ? "h4" : "h3";
  return <Tag className={className}>{name}</Tag>;
}

export function PlanExerciseItem({
  exercise,
  index,
  findById,
  getExerciseName,
  showLibraryDetails = true,
  linkExerciseNames = false,
}) {
  const name = resolveExerciseDisplayName(exercise, findById, getExerciseName);
  const library = showLibraryDetails ? findById(exercise?.exercise) : null;
  const coachNotes =
    typeof exercise?.notes === "string" && exercise.notes.trim()
      ? exercise.notes.trim()
      : null;

  return (
    <article className="pp-exercise-item">
      <header className="pp-exercise-header">
        <span className="pp-exercise-index">{index + 1}</span>
        <ExerciseTitle
          exerciseId={exercise?.exercise}
          name={name}
          linkExerciseNames={linkExerciseNames}
        />
      </header>

      <PrescriptionGrid exercise={exercise} />

      {coachNotes ? <NotesBlock label="Coach notes" content={coachNotes} /> : null}
      {library?.instructions ? (
        <NotesBlock label="Instructions" content={library.instructions} />
      ) : null}
      {library?.description && !library?.instructions ? (
        <NotesBlock label="Description" content={library.description} />
      ) : null}

      <PlanAlternatives
        alternatives={exercise?.alternatives}
        findById={findById}
        getExerciseName={getExerciseName}
        linkExerciseNames={linkExerciseNames}
      />
    </article>
  );
}

export function PlanSuperSetItem({
  exercise,
  index,
  findById,
  getExerciseName,
  linkExerciseNames = false,
}) {
  const summary = buildPrescriptionSummary(exercise);
  const subExercises = exercise?.exercises ?? [];

  return (
    <article className="pp-exercise-item pp-superset">
      <header className="pp-exercise-header pp-superset-header">
        <span className="pp-exercise-index">{index + 1}</span>
        <div>
          <h3 className="pp-superset-title">
            Superset: {exercise?.superSetName || "Super Set"}
          </h3>
          <p className="pp-superset-hint">Perform exercises back-to-back each round</p>
        </div>
      </header>

      {(summary.summaryChips.length > 0 || summary.detailRows.length > 0) && (
        <PrescriptionGrid exercise={exercise} />
      )}
      {summary.summaryChips.length === 0 && summary.detailRows.length === 0 && (
        <p className="pp-superset-type">{getPrescriptionLabel(summary.type)}</p>
      )}

      <div className="pp-superset-exercises">
        {subExercises.map((sub, subIdx) => (
          <div key={sub.id ?? subIdx} className="pp-superset-child">
            <span className="pp-superset-letter">{String.fromCharCode(65 + subIdx)}</span>
            <div className="pp-superset-child-body">
              <ExerciseTitle
                exerciseId={sub?.exercise}
                name={resolveExerciseDisplayName(sub, findById, getExerciseName)}
                sub
                linkExerciseNames={linkExerciseNames}
              />
              <PrescriptionGrid exercise={sub} />
              {sub?.notes?.trim() ? (
                <NotesBlock label="Coach notes" content={sub.notes.trim()} />
              ) : null}
              <PlanAlternatives
                alternatives={sub?.alternatives}
                findById={findById}
                getExerciseName={getExerciseName}
                linkExerciseNames={linkExerciseNames}
              />
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

/** @deprecated Use PlanExerciseItem */
export const PrintableExerciseItem = PlanExerciseItem;

/** @deprecated Use PlanSuperSetItem */
export const PrintableSuperSetItem = PlanSuperSetItem;
